import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import moment from "moment";
import "moment/locale/ru";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Aviation } from "../../../../../services";
import CustomDatePicker from "../../../../shared/date-picker/Date";
import CalendarIcon from "../../../../shared/icons/CalendarIcon";
import ExpectedArrivalCalendar from "../../../../orders-new/order-info-expeditor/components/ExpectedArrivalBlock/ExpectedArrivalCalendar";
import style from "../../../../orders-new/order-info-expeditor/components/ExpectedArrivalBlock/ExpectedArrivalBlock.module.scss";

const AviationExpeditorExpectedArrivalBlock = ({
  order,
  fetchOrderData,
  errorText = "",
  onErrorChange,
  focusRequest = 0,
}) => {
  const { t } = useTranslation();
  const initialUnloadingDate = order?.expectedUnloadingDate || null;
  const [date, setDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const blockRef = useRef(null);

  useEffect(() => {
    setDate(initialUnloadingDate ? new Date(initialUnloadingDate) : null);
  }, [initialUnloadingDate, order?.id]);

  useEffect(() => {
    if (!focusRequest) return;
    blockRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    blockRef.current?.querySelector("input")?.focus({ preventScroll: true });
  }, [focusRequest]);

  const dateError = date ? "" : errorText;

  if (String(order?.status || "").toLowerCase() !== "onthetrip") return null;

  const handleDateChange = (nextDate) => {
    if (isLoading) return;
    setDate(nextDate);
    setIsCalendarOpen(false);
    if (nextDate) saveDate(nextDate);
  };

  const handleDateFieldClick = (event) => {
    if (isLoading || event.target.closest(".react-datepicker")) return;
    setIsCalendarOpen(true);
  };

  const saveDate = async (nextDate) => {
    if (isLoading) return;
    if (!nextDate || !order?.id) {
      toast.error(t("orderFilter.datePlaceholder"));
      return;
    }

    const previousDate = date;
    setIsLoading(true);
    try {
      await Aviation.updateExpectedUnloadingDate(order.id, {
        expectedUnloadingDate: nextDate.toISOString(),
      });
      toast.success(t("orderInfoExpeditor.expectedArrival.dateUpdated"));
      await fetchOrderData?.();
      onErrorChange?.("");
    } catch {
      setDate(previousDate);
      toast.error(t("orderInfoExpeditor.expectedArrival.updateDateError"));
    } finally {
      setIsLoading(false);
    }
  };

  if (initialUnloadingDate) {
    return (
      <div ref={blockRef} className={style.card}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>
            {t("orderInfoExpeditor.expectedArrival.aviationTitle")}
            <span aria-hidden="true">{"*"}</span>
          </span>
        </div>

        <div className={style.view_row}>
          <div className={style.view_date}>
            <CalendarIcon />
            <span>{moment(initialUnloadingDate).locale("ru").format("D MMMM YYYY")}</span>
          </div>
          <ExpectedArrivalCalendar
            selected={date}
            onChange={setDate}
            onClear={() => setDate(null)}
            onSelect={() => saveDate(date)}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={blockRef} className={style.card}>
      <div className={style.topbar}>
        <span className={style.topbar_title}>
          {t("orderInfoExpeditor.expectedArrival.aviationTitle")}
          <span aria-hidden="true">
            {"*"}
          </span>
        </span>
      </div>

      <div
        className={cn(style.date_field, {
          [style.date_field_error]: dateError,
        })}
        onClick={handleDateFieldClick}
      >
        <span
          className={cn(style.date_label, {
            [style.date_label_error]: dateError,
          })}
        >
          {t("orderFilter.datePlaceholder")}
        </span>
        <CustomDatePicker
          selected={date}
          placeholderText={t("orderFilter.datePlaceholder")}
          dateFormat="dd.MM.yyyy"
          onChange={handleDateChange}
          disabled={isLoading}
          open={isCalendarOpen}
          onClickOutside={() => setIsCalendarOpen(false)}
          errorText={dateError}
          ariaInvalid={Boolean(dateError)}
          popperProps={{ strategy: "fixed" }}
        />
        <CalendarIcon />
      </div>
    </div>
  );
};

export default AviationExpeditorExpectedArrivalBlock;
